/**
 * Back-office — Leads partenaire (permission partners.manage).
 * BCL prend en charge, convertit en dossier (seul BCL planifie : ARCEP) ou
 * rejette les fiches transmises par les partenaires.
 */
import type { Lead, LeadStatus } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import { leadStatusBadge, needTypeLabels } from '@/features/partners/lead-labels';
import {
  convertLead,
  listAllLeads,
  rejectLead,
  takeLead,
} from '@/features/partners/leads-api';

const filters: { value: LeadStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'recue', label: 'Reçus' },
  { value: 'prise_en_charge', label: 'Pris en charge' },
  { value: 'convertie', label: 'Convertis' },
  { value: 'rejetee', label: 'Rejetés' },
];

export function AdminLeadsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<LeadStatus | 'all'>('all');
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const leads = useQuery({
    queryKey: ['admin', 'leads', filter],
    queryFn: () => listAllLeads(filter === 'all' ? undefined : filter),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'leads'] });

  const takeMut = useMutation({
    mutationFn: (id: string) => takeLead(id),
    onSuccess: () => {
      toast.success('Lead pris en charge');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const convertMut = useMutation({
    mutationFn: (id: string) => convertLead(id),
    onSuccess: (r) => {
      toast.success('Dossier créé');
      invalidate();
      navigate(`/admin/projects/${r.projectId}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectLead(id, reason),
    onSuccess: () => {
      toast.success('Lead rejeté');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const busy = takeMut.isPending || convertMut.isPending || rejectMut.isPending;

  function renderActions(lead: Lead) {
    if (lead.status === 'convertie' || lead.status === 'rejetee') {
      return <span className="text-xs text-gray-500">—</span>;
    }
    return (
      <div className="flex items-center justify-end gap-2">
        {lead.status === 'recue' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => takeMut.mutate(lead.id)}>
            Prendre en charge
          </Button>
        )}
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700"
          disabled={busy}
          onClick={() => convertMut.mutate(lead.id)}
        >
          Convertir
        </Button>
        <Input
          placeholder="Motif"
          className="h-8 w-28"
          value={reasons[lead.id] ?? ''}
          onChange={(e) => setReasons({ ...reasons, [lead.id]: e.target.value })}
        />
        <Button
          size="sm"
          variant="outline"
          className="bg-red-50 text-red-800"
          disabled={busy || !reasons[lead.id]}
          onClick={() => rejectMut.mutate({ id: lead.id, reason: reasons[lead.id]! })}
        >
          Rejeter
        </Button>
      </div>
    );
  }

  return (
    <DashboardLayout variant="admin" title="Leads">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Leads partenaire</CardTitle>
            <CardDescription>
              {leads.data ? `${leads.data.length} lead(s)` : 'Chargement…'}
            </CardDescription>
          </div>
          <div className="flex gap-1">
            {filters.map((f) => (
              <Button
                key={f.value}
                size="sm"
                variant={filter === f.value ? 'default' : 'outline'}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Besoin</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.data?.length ? (
                leads.data.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">
                      {lead.clientFirstName} {lead.clientLastName}
                      <span className="block text-xs text-gray-500">
                        {lead.clientEmail}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {needTypeLabels[lead.needType]}
                    </TableCell>
                    <TableCell className="text-sm">
                      {lead.postalCode} {lead.city}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={leadStatusBadge[lead.status].cls}>
                        {leadStatusBadge[lead.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{renderActions(lead)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                    Aucun lead.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
